/**
 * Notification utility functions
 */

/**
 * Play notification sound
 * @param {string} type - Type of notification (info, success, warning, error)
 */
export const playNotificationSound = (type = 'info') => {
  try {
    // Check if audio is allowed and user hasn't disabled sounds
    const soundEnabled = localStorage.getItem('notificationSoundEnabled') !== 'false';
    if (!soundEnabled) return;

    // Create audio context for notification sounds
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    // Different frequencies for different notification types
    const frequencies = {
      info: 800,
      success: 600,
      warning: 1000,
      error: 400
    };

    const frequency = frequencies[type] || frequencies.info;
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.value = frequency;
    oscillator.type = 'sine';

    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.3);
  } catch (error) {
    console.warn('Could not play notification sound:', error);
  }
};

/**
 * Play custom sound file
 * @param {string} soundPath - Path to the sound file (e.g., '/sounds/notification.mp3')
 * @param {number} volume - Volume level (0.0 to 1.0, default: 0.5)
 */
export const playCustomSound = (soundPath, volume = 0.5) => {
  try {
    // Check if audio is allowed and user hasn't disabled sounds
    const soundEnabled = localStorage.getItem('notificationSoundEnabled') !== 'false';
    if (!soundEnabled) return;

    // Create audio element
    const audio = new Audio(soundPath);
    audio.volume = Math.max(0, Math.min(1, volume)); // Clamp volume between 0 and 1
    
    // Play the sound
    audio.play().catch((error) => {
      console.warn('Could not play custom sound file:', error);
      // Fallback to default notification sound if custom sound fails
      playNotificationSound('info');
    });
  } catch (error) {
    console.warn('Could not play custom sound:', error);
    // Fallback to default notification sound if custom sound fails
    playNotificationSound('info');
  }
};

/**
 * Request browser notification permission
 * @returns {Promise<boolean>} - True if permission granted
 */
export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    console.warn('This browser does not support notifications');
    return false;
  }

  if (Notification.permission === 'granted') {
    return true;
  }

  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }

  return false;
};

/**
 * Show browser notification
 * @param {Object} notification - Notification object
 * @param {Function} onClose - Callback when notification is closed
 * @param {Function} onClick - Callback when notification is clicked
 */
export const showBrowserNotification = (notification, onClose, onClick) => {
  if (!('Notification' in window) || Notification.permission !== 'granted') {
    return;
  }

  // Check if user has enabled browser notifications
  const browserNotificationsEnabled = localStorage.getItem('browserNotificationsEnabled') !== 'false';
  if (!browserNotificationsEnabled) {
    return;
  }

  try {
    const title = notification.title || 'New Notification';
    const options = {
      body: notification.description || '',
      icon: '/logo/logo_single.svg',
      badge: '/logo/logo_single.svg',
      tag: notification.id || `notification-${Date.now()}`,
      requireInteraction: notification.mode === 'ERROR' || notification.mode === 'WARNING',
      data: {
        notificationId: notification.id,
        relatedEntityUrl: notification.relatedEntityUrl,
        jsonRecord: notification.jsonRecord
      }
    };

    const browserNotification = new Notification(title, options);

    browserNotification.onclick = (event) => {
      event.preventDefault();
      window.focus();
      
      if (onClick) {
        onClick(notification);
      }
      
      browserNotification.close();
    };

    browserNotification.onclose = () => {
      if (onClose) {
        onClose();
      }
    };

    // Auto-close after 5 seconds (unless it's an error/warning)
    if (options.requireInteraction === false) {
      setTimeout(() => {
        browserNotification.close();
      }, 5000);
    }
  } catch (error) {
    console.error('Error showing browser notification:', error);
  }
};

/**
 * Check if notifications should be shown (respects DND mode)
 * @returns {boolean}
 */
export const shouldShowNotification = () => {
  // Check for Do Not Disturb mode
  const dndEnabled = localStorage.getItem('notificationDNDEnabled') === 'true';
  if (dndEnabled) {
    const dndStart = localStorage.getItem('notificationDNDStart');
    const dndEnd = localStorage.getItem('notificationDNDEnd');
    
    if (dndStart && dndEnd) {
      const now = new Date();
      const start = new Date(dndStart);
      const end = new Date(dndEnd);
      
      // Handle overnight DND (e.g., 22:00 - 08:00)
      if (start > end) {
        if (now >= start || now <= end) {
          return false;
        }
      } else {
        if (now >= start && now <= end) {
          return false;
        }
      }
    } else if (dndEnabled) {
      // DND enabled but no time range set - disable all notifications
      return false;
    }
  }
  
  return true;
};

/**
 * Get notification preferences
 * @returns {Object}
 */
export const getNotificationPreferences = () => {
  return {
    soundEnabled: localStorage.getItem('notificationSoundEnabled') !== 'false',
    browserNotificationsEnabled: localStorage.getItem('browserNotificationsEnabled') !== 'false',
    dndEnabled: localStorage.getItem('notificationDNDEnabled') === 'true',
    dndStart: localStorage.getItem('notificationDNDStart'),
    dndEnd: localStorage.getItem('notificationDNDEnd')
  };
};

/**
 * Save notification preferences
 * @param {Object} preferences - Preferences object
 */
export const saveNotificationPreferences = (preferences) => {
  if (preferences.soundEnabled !== undefined) {
    localStorage.setItem('notificationSoundEnabled', preferences.soundEnabled.toString());
  }
  if (preferences.browserNotificationsEnabled !== undefined) {
    localStorage.setItem('browserNotificationsEnabled', preferences.browserNotificationsEnabled.toString());
  }
  if (preferences.dndEnabled !== undefined) {
    localStorage.setItem('notificationDNDEnabled', preferences.dndEnabled.toString());
  }
  if (preferences.dndStart !== undefined) {
    localStorage.setItem('notificationDNDStart', preferences.dndStart);
  }
  if (preferences.dndEnd !== undefined) {
    localStorage.setItem('notificationDNDEnd', preferences.dndEnd);
  }
};

